export interface NotificationHandler {
  id: string;
  groupId: string;
  eventType: string;
  channelConfig: ChannelConfig;
  enabled: boolean;
  name: string;
  tmp: {
    eventTypes: [];
    handlerChannelTypes: Record<ChannelConfigType, HandlerChannelTypeInfo>;
    channelTypes: [];
    templateList: NotificationTemplate[];
    templateTypes: [];
    recipientTypes: [];
    placeholders: [];
    recipientValues: {};
  };
}
export interface HandlerChannelTypeAttribute {
  name: string;
  description: string;
  type: string;
  isSecret: boolean;
  isRequired: boolean;
  isTemplateId: boolean;
  defaultValue: string;
  allowedValues: string[];
  supportsTemplateExpressions: boolean;
  templateTypes: string[] | null;
}

export interface HandlerChannelTypeInfo {
  description: string;
  attributes: HandlerChannelTypeAttribute[];
}

export enum TemplateTypeEnum {
  Undefined = 'Undefined',
  Html = 'Html',
  PlainText = 'PlainText',
  Json = 'Json',
}
export enum ChannelConfigType {
  Unknown = 'Unknown',

  // Email channels
  SESEmail = 'SESEmail',
  MailChimpEmail = 'MailChimpEmail',
  SailThruEmail = 'SailThruEmail',

  // Push notification channels
  FirebasePushNotification = 'FirebasePushNotification',

  // Other channels
  Webhook = 'Webhook',
}
export interface BaseChannelConfig {
  channelType: ChannelConfigType;
}

export interface SESEmailChannelConfig extends BaseChannelConfig {
  channelType: ChannelConfigType.SESEmail;
  subject: string;
  fromEmail: string;
  templateGroupId?: string;
  templateId?: string;
}

export interface MailChimpEmailChannelConfig extends BaseChannelConfig {
  channelType: ChannelConfigType.MailChimpEmail;
  apiKey: string;
  subject: string;
  fromEmail: string;
  templateName?: string;
  templateAttributeMap?: Record<string, string>;
}

export interface SailThruEmailChannelConfig extends BaseChannelConfig {
  channelType: ChannelConfigType.SailThruEmail;
  apiKey: string;
  apiSecret: string;
  subject: string;
  fromEmail: string;
  templateName?: string;
  templateAttributeMap?: Record<string, string>;
}

export interface FirebasePushChannelConfig extends BaseChannelConfig {
  channelType: ChannelConfigType.FirebasePushNotification;
}

export interface WebhookChannelConfig extends BaseChannelConfig {
  channelType: ChannelConfigType.Webhook;
  url: string;
  method: string;
  headers?: Record<string, string>;
  template?: string;
}

export type ChannelConfig =
  | SESEmailChannelConfig
  | MailChimpEmailChannelConfig
  | SailThruEmailChannelConfig
  | FirebasePushChannelConfig
  | WebhookChannelConfig;

// Recipient types
export enum RecipientType {
  // Unknown = 'Unknown',
  UserId = 'UserId',
  OUId = 'OUId',
  Email = 'Email',
}

// Recipient config
export interface RecipientConfig {
  recipientType: RecipientType;
  value: string;
}

// Notification DTO
export interface NotificationDto {
  id: string;
  groupId: string;
  eventType: string;
  content: string;
  recipients: RecipientConfig[];
  sendDateUtc: string | Date;
  sendOption: 'now' | 'later';
  /**
   * Additional attributes that can be used in the notification content.
   * These can include template variables or other metadata.
   */
  attributes: {
    platform: 'all' | 'ios' | 'android';
    oUId: string;
    powrSiteCode: string;
    siteId: string;
    siteUrl: string;
    siteLogoUrl: string;
    userId: string;
    userEmail: string;
    contentType?: 'none' | 'video' | 'series' | 'episode' | 'movie' | 'channel' | 'liveEvent' | 'liveGuide' | 'epg' | 'url';
    videoId?: string;
    seriesId?: string;
    siteTitle: string;
    channelId?: string;
    url?: string;
    epgId?: string;
    liveGuideId?: string;
  };
  tmp: NotificationTmp;
}
export interface PushNotificationDto {
  id: string;
  groupId: string;
  eventType: string;
  content: string;
  recipients: RecipientConfig[];
  sendDateUtc: string | Date;
  sendOption: 'now' | 'later';
  /**
   * Additional attributes that can be used in the notification content.
   * These can include template variables or other metadata.
   */
  attributes: {
    platform: 'all' | 'ios' | 'android';
    oUId: string;
    powrSiteCode: string;
    siteId: string;
    siteUrl: string;
    siteLogoUrl: string;
    userId: string;
    userEmail: string;
    contentType?: 'none' | 'video' | 'series' | 'episode' | 'movie' | 'channel' | 'liveEvent' | 'liveGuide' | 'epg' | 'url';
    videoId?: string;
    seriesId?: string;
    siteTitle: string;
    channelId?: string;
    url?: string;
    epgId?: string;
    liveGuideId?: string;
  };
  tmp: NotificationTmp;
}

interface NotificationTmp {
  notificationConfig: NotificationConfig;
  notificationEventTypes: string[];
  attributesList: [];
  templateTypes: string[];
  eventTypes: string[];
}
// Template engine enum
export enum TemplateEngine {
  Handlebars = 'Handlebars',
}

// Notification template DTO
export interface NotificationTemplate {
  id: string;
  name: string;
  groupId: string;
  template: string;
  engine: TemplateEngine;
  templateType: TemplateTypeEnum;
  tmp: {
    templateTypes: [];
    recipientTypes: [];
    placeholders: [];
    sampleTemplate: '';
    defaultTemplates: [];
  };
}

// Notification template list item DTO
export interface NotificationTemplateListItemDto {
  id: string;
  name: string;
  groupId: string;
}

export enum ExternalNotificationEventTypes {
  PasswordReset = 'PasswordReset',
  PasswordResetConfirmation = 'PasswordResetConfirmation',
  AppUserMarketingNotification = 'AppUserMarketingNotification',
  AppUserLogin = 'AppUserLogin',
  AppUserLogout = 'AppUserLogout',
  AppUserSignUp = 'AppUserSignUp',
  AppUserDeleted = 'AppUserDeleted',
  AppUserTVRegistered = 'AppUserTVRegistered',
}
export interface NotificationConfig {
  handlerChannelTypes: Record<ChannelConfigType, HandlerChannelTypeInfo>;
  eventTypes: Record<ExternalNotificationEventTypes, EventTypeInfo>;
  templateTypes: Record<string, string>;
  recipientTypes: Record<string, string>;
}

export interface EventTypeInfo {
  description: string;
  attributes: string[];
}

export interface NotificationLog {
  id: string;
  groupId: string;
  handlerId: string;
  handlerName: string;
  eventType: ExternalNotificationEventTypes;
  channelType: ChannelConfigType;
  recipientType: RecipientType;
  recipient: string;
  content: string;
  attributes: Record<string, string>;
  status: string;
  reason: string;
  sentDateUtc: Date;
  receivedDateUtc: Date;
}

export interface NotificationLogList {
  hasMore: boolean;
  notificationLogs: NotificationLog[];
  skip: number;
  take: number;
  totalCount: number;
}
